'use client';

import { useCallback, useState, type ReactNode } from 'react';

import { recordEnquiryReveal } from '@/features/enquiry/api';

export interface PhoneRevealProps {
  phone: string;
  phoneHref?: string;
  /** Classes applied to the "Show number" button (hidden state). */
  hiddenClassName?: string;
  /** Classes applied to the phone link (revealed state). */
  shownClassName?: string;
  /** Optional class for the phone text span in the revealed state. */
  phoneClassName?: string;
  /** Optional icon displayed before the label / phone number. */
  icon?: ReactNode;
  /** Custom content for the hidden button. Defaults to "Show number". */
  children?: ReactNode;
  /** Associated listing ID if revealed within a listing context. */
  listingId?: number | string;
  /** Optional metadata associated with the reveal interaction. */
  metadata?: Record<string, unknown>;
  /** Optional callback fired when reveal is triggered. */
  onReveal?: () => void;
}

export function PhoneReveal({
  phone,
  phoneHref,
  hiddenClassName,
  shownClassName,
  phoneClassName,
  icon,
  children,
  listingId,
  metadata,
  onReveal,
}: PhoneRevealProps) {
  const [revealed, setRevealed] = useState(false);

  const handleReveal = useCallback(() => {
    if (!revealed) {
      setRevealed(true);
      onReveal?.();

      const numericListingId: number | undefined = (() => {
        if (typeof listingId === 'number' && Number.isFinite(listingId) && listingId > 0) {
          return Math.floor(listingId);
        }
        if (typeof listingId === 'string') {
          const trimmed = listingId.trim();
          const directNum = Number(trimmed);
          if (Number.isFinite(directNum) && directNum > 0) {
            return Math.floor(directNum);
          }
          const digits = trimmed.replace(/\D/g, '');
          if (digits) {
            const parsed = parseInt(digits, 10);
            if (Number.isFinite(parsed) && parsed > 0) {
              return parsed;
            }
          }
        }
        return undefined;
      })();

      void recordEnquiryReveal({
        source: 'PHONE_REVEAL',
        ...(numericListingId !== undefined ? { listingId: numericListingId } : {}),
        metadata: {
          page: typeof window !== 'undefined' ? window.location.pathname : undefined,
          ...(listingId !== undefined ? { listingId: String(listingId) } : {}),
          ...metadata,
        },
      });
    }
  }, [revealed, listingId, metadata, onReveal]);

  const resolvedPhoneHref = phoneHref ?? `tel:${phone.replace(/\s+/g, '')}`;

  if (!revealed) {
    return (
      <button
        type="button"
        onClick={handleReveal}
        className={hiddenClassName ?? 'cursor-pointer underline transition hover:text-white'}
      >
        {icon}
        {children ?? <span>Show number</span>}
      </button>
    );
  }

  return (
    <a href={resolvedPhoneHref} className={shownClassName}>
      {icon}
      <span className={phoneClassName}>{phone}</span>
    </a>
  );
}
